import { BusinessObjectBase, ModelBase, ObservableCollection } from '@skylab-shared/core';
import { AddressModel, ClientModel, DiscountKind, DocumentClientModel, SaleDocumentItemModel, SaleDocumentKind } from '@skylab-shared/dataModel';
import { ClientRepositoryBase } from '../../client/client.repo-base';
import { ContextKeys } from '../../common/context-keys';
import { documentDeliveryAddressFilled } from '../../common/serialziation-utils';
import { CommunityTransactionForSaleQuery } from '../../community-transaction/queries/community-transaction-for-sale.query-base';
import { CompanyParametersQuery } from '../../company/queries/company-parameters.query-base';
import { CountryCollectionQuery } from '../../country/queries/country.query-base';
import { fillDocumentClientBasedOnClient, getUpdatedBuyerAsync } from '../../documents/utils/document-client-utils';
import { SaleKindQuery } from '../queries/sale-kind.query-base';
import { isWstoTransaction } from '../utils/sale-utils';

interface ModelPart extends ModelBase {
    client: DocumentClientModel | null;
    buyer: DocumentClientModel | null;
    items: ObservableCollection<SaleDocumentItemModel>;
    paymentMethodId: number;
    dueDateInDays: number;
    deliveryAddress: AddressModel | null;
    communityTransactionId: number;
    wstoConsumerCountryId: number | null;
    kind: SaleDocumentKind;
}

export abstract class ClientMixin<TModel extends ModelPart> extends BusinessObjectBase<TModel>  {
    private client: ClientModel | null = null;
    protected clientRepository: ClientRepositoryBase;

    public async loadSourceClient(): Promise<void> {
        if (this.model.client && this.model.client.sourceClientId) {
            this.client = await this.loadClientAsync(this.model.client.sourceClientId);
        }
    }

    public setClientOnDocument(client: ClientModel): void {
        const documentClient = new DocumentClientModel();
        this.model.client = documentClient;
        fillDocumentClientBasedOnClient(documentClient, client);
    }

    public async setClientRelatedProperties(client: ClientModel, setBuyer = false): Promise<void> {
        this.client = client;

        if (setBuyer) {
            this.model.buyer = await getUpdatedBuyerAsync(client, this.localContext, this.clientRepository);
        }
        if (client.deliveryAddress) {
            this.model.deliveryAddress = client.deliveryAddress;
        }

        this.applyDiscountForClient();
        this.setPaymentMethodForClient();
        this.setWstoValuesForClient();
    }

    public clearClient(): void {
        this.client = null;
        this.model.client = null;
        this.model.buyer = null;
        this.model.deliveryAddress = new AddressModel();
        this.model.deliveryAddress.countryId = this.localContext.firstPreloaded(CountryCollectionQuery, { default: true }).id;
        this.model.wstoConsumerCountryId = null;
    }

    protected applyDiscountForClient(): void {
        this.model.items.forEach(item => this.applyClientDiscountToItem(item));
    }

    public applyClientDiscountToItem(item: SaleDocumentItemModel): void {
        if (this.discountForClientDefined) {
            item.discountKind = DiscountKind.Percentage;
            if (this.client?.defaultDiscount) item.discountValue = this.client.defaultDiscount;
        }
    }

    protected setPaymentMethodForClient(): void {
        if (this.paymentMethodForClientDefined) {
            if (this.client?.defaultPaymentMethodId) this.model.paymentMethodId = this.client.defaultPaymentMethodId;
            if (this.client?.defaultDueDate) this.model.dueDateInDays = this.client.defaultDueDate;
        }
    }

    protected setWstoValuesForClient(): void {
        if (isWstoTransaction(this.localContext.getPreloaded(CommunityTransactionForSaleQuery), this.model.communityTransactionId)) {
            this.model.wstoConsumerCountryId = this.getWstoCountryId(this.client);
        }
    }

    protected get discountForClientDefined(): boolean {
        return !!(this.model.client && this.client && !!this.client.defaultDiscount);
    }

    public get paymentMethodForClientDefined(): boolean {
        return !!(this.model.client && this.client && !!this.client.defaultPaymentMethodId && this.client.defaultDueDate !== null);
    }

    private getWstoCountryId(client: ClientModel | null): number | null {
        const { systemCountryId } = this.localContext.getPreloaded(CompanyParametersQuery);
        const countries = this.localContext.filterPreloaded(CountryCollectionQuery, { euMember: true }).filter(c => c.id !== systemCountryId);

        const isDeliveryAddressFilled = documentDeliveryAddressFilled(this.model.deliveryAddress);
        const countryId = (isDeliveryAddressFilled && this.model.deliveryAddress?.countryId) || client?.countryId;

        const wstoCountry = countries.find(c => c.id === countryId);

        return wstoCountry?.id || null;
    }

    private loadClientAsync(id: number): Promise<ClientModel> {
        return this.localContext.getAsync(ContextKeys.client(id), () => this.clientRepository.findAsync(id));
    }

    public resetDefaultPaymentMethodAndDueDate(): void {
        const documentConfiguration = this.localContext.firstPreloaded(SaleKindQuery, { name: this.model.kind });
        this.model.paymentMethodId = documentConfiguration.defaultPaymentMethodId;
        this.model.dueDateInDays = documentConfiguration.defaultDueDate;
    }
}
